import type { CSSProperties } from 'react';

// Shared potted-plant product graphic for the storefront previews. Decorative by default; pass `title`
// to label it. Pot defaults to the platform brand colour (`potColor` overrides); `mini` gives a shape
// legible at thumbnail sizes.
export function PlantGraphic({
  className,
  potColor,
  title,
  mini,
}: {
  className?: string;
  potColor?: string;
  title?: string;
  mini?: boolean;
}) {
  const potStyle: CSSProperties = { fill: potColor ?? 'var(--color-platform-primary)' };
  const viewBox = mini ? '0 0 24 24' : '0 0 64 64';
  const shapes = mini ? <MiniShapes potStyle={potStyle} /> : <PlantShapes potStyle={potStyle} />;

  // Two branches so the svg is statically either labelled (role + title) or decorative (aria-hidden).
  if (title) {
    return (
      <svg viewBox={viewBox} className={className} role="img">
        <title>{title}</title>
        {shapes}
      </svg>
    );
  }
  return (
    <svg viewBox={viewBox} className={className} aria-hidden="true">
      {shapes}
    </svg>
  );
}

// Detailed plant for the hero/price sizes — a full leafy potted plant.
function PlantShapes({ potStyle }: { potStyle: CSSProperties }) {
  return (
    <>
      <path d="M32 36 V20" stroke="#6fa84f" strokeWidth="2" strokeLinecap="round" />
      <ellipse cx="32" cy="15" rx="5" ry="11" fill="#7fb069" />
      <ellipse cx="22" cy="23" rx="9" ry="5" fill="#8fbf6e" transform="rotate(-28 22 23)" />
      <ellipse cx="42" cy="23" rx="9" ry="5" fill="#6fa84f" transform="rotate(28 42 23)" />
      <ellipse cx="24" cy="31" rx="8" ry="4.5" fill="#9bcb7b" transform="rotate(-18 24 31)" />
      <ellipse cx="40" cy="31" rx="8" ry="4.5" fill="#7fb069" transform="rotate(18 40 31)" />
      <path d="M22 39 h20 l-2.5 17 h-15 z" style={potStyle} />
      <rect x="20" y="36" width="24" height="5" rx="2" style={potStyle} />
    </>
  );
}

// Simplified plant — three leaves and a chunky pot — that reads cleanly at thumbnail sizes.
function MiniShapes({ potStyle }: { potStyle: CSSProperties }) {
  return (
    <>
      <ellipse cx="12" cy="7" rx="2.2" ry="4.5" fill="#34c77b" />
      <ellipse cx="8" cy="10" rx="3.4" ry="2" fill="#46d88c" transform="rotate(-25 8 10)" />
      <ellipse cx="16" cy="10" rx="3.4" ry="2" fill="#27a867" transform="rotate(25 16 10)" />
      <path d="M8 15h8l-1.2 7h-5.6z" style={potStyle} />
      <rect x="7" y="13.5" width="10" height="2" rx="1" style={potStyle} />
    </>
  );
}
